import { Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  catchError,
  concatMap,
  exhaustMap,
  map,
  take,
  tap,
} from 'rxjs/operators';
import { of } from 'rxjs';

import {
  BrandsService,
  GetBrandBriefResponse,
  GetBrandListResponse,
  BrandDetails,
  BrandIntegrationType,
  GetBrandNamesFromBrandIdsParams,
  GetBrandNamesFromBrandIdsResponse,
} from '../../proto/brandassador';
import { BrandsState } from './brands.reducer';
import {
  actionBrandGet,
  actionBrandGetEmpty,
  actionBrandGetFailure,
  actionBrandGetSuccess,
  actionBrandNamesListGet,
  actionBrandNamesListGetFailure,
  actionBrandNamesListGetSuccess,
  actionBrandsListGet,
  actionBrandsListGetSuccess,
  BrandsActionTypes,
} from './brands.actions';
import { OxioAdminBrandService } from '../..';
import { select, Store } from '@ngrx/store';
import { AccountState, isOxioAdmin } from '../account';
import { UtilsService } from '../../services';

@Injectable({
  providedIn: 'root',
})
export class BrandsEffects {
  constructor(
    private brandsService: BrandsService,
    private actions$: Actions,
    private oxioAdminBrandService: OxioAdminBrandService,
    private utils: UtilsService,
    private store: Store<AccountState>
  ) {}

  /**
   * Get brand brief
   */
  getBrand$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actionBrandGet),
      exhaustMap((action) => {
        const brandUuid = action.brandUuid;
        if (brandUuid) {
          return this.brandsService.getBrandBrief(brandUuid).pipe(
            map((res: GetBrandBriefResponse) => {
              this.store
                .pipe(
                  select(isOxioAdmin),
                  tap((isAdmin) => {
                    if (isAdmin)
                      this.oxioAdminBrandService.saveBrandUuid(brandUuid);
                  }),
                  take(1)
                )
                .subscribe();

              return actionBrandGetSuccess({
                payload: res.brand,
                brandUuid,
              });
            }),
            catchError(() => of(actionBrandGetFailure()))
          );
        }
        this.oxioAdminBrandService.destroyBrandUuid();
        return of(actionBrandGetEmpty());
      })
    );
  });

  /**
   * Get the list of brands
   */
  getBrandsList$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actionBrandsListGet),
      exhaustMap(() => {
        return this.brandsService.getBrandList().pipe(
          map((res: GetBrandListResponse) => {
            return actionBrandsListGetSuccess({
              payload: this.utils.filterOutTestBrands(res.brands),
            });
          }),
          catchError(() => of(actionBrandGetFailure()))
        );
      })
    );
  });

  /**
   * Get the list of brand names
   */
  getBrandNamesList$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actionBrandNamesListGet),
      exhaustMap(params => {
        return this.brandsService
          .getBrandNamesFromBrandIds(params.body)
          .pipe(
            map((res: GetBrandNamesFromBrandIdsResponse) => {
              return actionBrandNamesListGetSuccess({
                payload: res
              });
            },
            catchError(() => of(actionBrandNamesListGetFailure()))
          ));
      })
    );
  });
}
